<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useDebounceFn, useEventListener } from '@vueuse/core';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import { usePersistentTable } from '@/composables/usePersistentTable';
import { useEntityStore } from '@/stores/entityStore';
import { areaEntity } from '@/navigation/areas';
import { listSearchRule, listSearchText, withListSearch } from '@/utils/listSearch';
import { hasQuickSearch } from '@/utils/quickSearchFields';
import { shortcutLabel } from '@/utils/shortcutLabel';
import QuickSearchFieldsChip from '@/components/search/QuickSearchFieldsChip.vue';
import QuickSearchFieldsPanel from '@/components/search/QuickSearchFieldsPanel.vue';

/**
 * The rounded search box at the left of a list's toolbar. Typing narrows the list by
 * the record type's quick search fields (SAU-467), for example a contact's name and
 * email. What you type becomes one ordinary filter rule (see utils/listSearch.ts), so
 * export and bulk actions act on the same rows. The rule is not a chip and is not
 * counted on the funnel, because this box already shows it.
 *
 * The "5 fields" chip opens the row of field tags under the box, where the person picks
 * the fields and Matches all or Matches any. The choice is theirs for the record type.
 *
 * ⌘F, or Ctrl+F on Windows, focuses it. The shortcut is only taken while a list
 * is on screen, because this box only exists there; everywhere else the browser
 * keeps its own find.
 */

const entityStore = useEntityStore();
const { filter, setFilter } = usePersistentTable();

const entityId = computed(() => entityStore.selectedEntityId);
const entityLabel = computed(() => areaEntity(entityId.value)?.label.toLowerCase() ?? 'this list');
const showsSearch = computed(() => hasQuickSearch(entityId.value));

const text = ref(listSearchText(filter.value));
watch(filter, (current) => {
    const fromFilter = listSearchText(current);
    if (fromFilter !== text.value.trim()) {
        text.value = fromFilter;
    }
});

const applySearch = useDebounceFn((value: string) => {
    const trimmed = value.trim();
    setFilter(withListSearch(filter.value, trimmed === '' ? null : listSearchRule(trimmed)));
}, 300);

function onInput(event: Event) {
    text.value = (event.target as HTMLInputElement).value;
    applySearch(text.value);
}

function clear() {
    text.value = '';
    setFilter(withListSearch(filter.value, null));
}

const inputEl = ref<HTMLInputElement | null>(null);
useEventListener(window, 'keydown', (event: KeyboardEvent) => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'f' && inputEl.value) {
        event.preventDefault();
        inputEl.value.focus();
        inputEl.value.select();
    }
});

// The tag row opens in a popover anchored under the box. The toolbar clips anything
// that overflows it, so the row is drawn above the page rather than inside the toolbar.
const fieldsOpen = ref(false);
const panelId = computed(() => `list-search-fields-${entityId.value}`);

const findShortcut = shortcutLabel('F');
</script>

<template>
  <Popover v-if="showsSearch" v-model:open="fieldsOpen">
    <PopoverAnchor as-child>
    <label
      class="flex h-9 w-full min-w-32 max-w-md items-center gap-2 rounded-full bg-background ps-3.5 pe-1.5 text-sm text-muted-foreground shadow-[inset_0_0_0_1px_var(--border)] focus-within:shadow-[inset_0_0_0_1px_var(--ring)]"
    >
      <MagnifyingGlassIcon class="size-4 shrink-0" aria-hidden="true" />
      <input
        ref="inputEl"
        type="search"
        data-testid="list-search-input"
        :value="text"
        :placeholder="`Search ${entityLabel}`"
        :aria-label="`Search ${entityLabel}`"
        class="min-w-0 flex-1 bg-transparent text-foreground outline-none placeholder:text-muted-foreground [&::-webkit-search-cancel-button]:hidden"
        @input="onInput"
        @keydown.escape="clear"
      />
      <button v-if="text" type="button" aria-label="Clear search" class="rounded-full p-0.5 hover:text-foreground" @click="clear">
        <XMarkIcon class="size-3.5" aria-hidden="true" />
      </button>
      <kbd v-else class="hidden text-xs sm:inline">{{ findShortcut }}</kbd>
      <PopoverTrigger as-child>
        <QuickSearchFieldsChip v-model:open="fieldsOpen" :entity-ids="[entityId]" :panel-id="panelId" triggered-by-parent />
      </PopoverTrigger>
    </label>
    </PopoverAnchor>
    <PopoverContent
      align="start"
      :side-offset="6"
      :collision-padding="8"
      class="w-[min(32rem,calc(100vw-16px))] p-3"
      data-testid="list-search-fields-popover"
      @open-auto-focus.prevent
    >
      <QuickSearchFieldsPanel :id="panelId" :entity-ids="[entityId]" />
    </PopoverContent>
  </Popover>
</template>
